/**
 * R4: the four groups, their twenty rows and each row's three independent
 * channel controls. A locked channel renders disabled, never hidden.
 *
 * `savedKey` is the one piece of local state this component holds, added
 * after the initial pass shipped with none. `error-modals`'s always-on rule
 * says a successful save is announced with `SavedNote`, never a toast, and
 * `apps/app/src/features/privacy/PrivacyCard.tsx` is this repo's own working
 * precedent for exactly that: a single "which field just saved" slot,
 * cleared at the START of the next change and set on the next success. This
 * mirrors that pattern exactly, keyed by `${alertKind}:${channel}` instead
 * of a fixed field-name union. It is not a second copy of server state --
 * `checked` still comes from `cell.isEnabled` alone.
 *
 * ISSUE-1032: the four groups render as disclosure rows that start
 * collapsed, per `.agents/views/43-notification-settings-levels.html`, so a
 * fan never faces a wall of switches. The open set is local view state from
 * `alertGroupDisclosure.ts`, and it survives a save because the route keeps
 * this component mounted across `settings.refetch()`.
 */
import { useNotificationSettingsSetAlertChannel } from "@sportsmet/notification-sdk/private";
import { Card, Checkbox, cn, FOCUS_RING, SavedNote } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  alertGroupCountLabel,
  INITIAL_EXPANDED_GROUPS,
  isGroupExpanded,
  toggleGroup,
} from "./alertGroupDisclosure";
import { CHANNEL_COLUMNS, type typeAlertGroup } from "./alertKindLabels";
import type { typeAlertGroupModel } from "./notificationSettingsState";

export const AlertGroupList = ({
  groups,
  onSaved,
}: {
  groups: readonly typeAlertGroupModel[];
  onSaved: () => void;
}): ReactNode => {
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<typeAlertGroup[]>([
    ...INITIAL_EXPANDED_GROUPS,
  ]);

  const setChannel = useNotificationSettingsSetAlertChannel({
    onSuccess: (result, variables) => {
      if (!showApiError(result)) {
        setSavedKey(`${variables.alertKind}:${variables.channel}`);
      }
      onSaved();
    },
  });

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => {
        const isExpanded = isGroupExpanded({
          expandedGroups,
          group: group.group,
        });
        return (
          <Card
            key={group.group}
            data-testid={`notification-group-${group.group}`}
          >
            <h2 className="text-h2 text-ink">
              <button
                type="button"
                data-testid={`notification-group-toggle-${group.group}`}
                aria-expanded={isExpanded}
                className={cn(
                  "flex min-h-touch-target w-full items-center gap-2 text-left",
                  FOCUS_RING,
                )}
                onClick={() => {
                  setExpandedGroups((prev) =>
                    toggleGroup({ expandedGroups: prev, group: group.group }),
                  );
                }}
              >
                <span className="flex-1">{group.header}</span>{" "}
                <span className="text-caption text-ink-secondary">
                  {alertGroupCountLabel(group.rows.length)}
                </span>
                <svg
                  aria-hidden="true"
                  focusable="false"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-icon-sm w-icon-sm shrink-0 text-ink-secondary"
                >
                  <path d={isExpanded ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"} />
                </svg>
              </button>
            </h2>
            {isExpanded ? (
              <div
                className="flex flex-col gap-4"
                data-testid={`notification-group-panel-${group.group}`}
              >
                {group.rows.map((row) => (
                  <div key={row.alertKind} className="flex flex-col gap-2">
                    <h3 className="text-body text-ink">{row.label}</h3>
                    <div className="flex flex-wrap gap-4">
                      {row.cells.map((cell) => {
                        const key = `${row.alertKind}:${cell.channel}`;
                        return (
                          <div
                            key={cell.channel}
                            data-testid={`alert-${row.alertKind}-${cell.channel}`}
                            className="flex flex-col gap-1"
                          >
                            <Checkbox
                              label={
                                CHANNEL_COLUMNS.find(
                                  (column) => column.channel === cell.channel,
                                )?.label ?? cell.channel
                              }
                              checked={cell.isEnabled}
                              disabled={cell.isLocked || setChannel.isPending}
                              description={cell.caption}
                              className={
                                cell.isDimmed ? "opacity-50" : undefined
                              }
                              onCheckedChange={(next) => {
                                setSavedKey(null);
                                setChannel.mutate({
                                  alertKind: row.alertKind,
                                  channel: cell.channel,
                                  isEnabled: next === true,
                                });
                              }}
                            />
                            <SavedNote saved={savedKey === key} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
};
